/**
 * The portal signs its `LoginResponse` into the handoff token, so `playerId` is
 * readable from the URL before the gateway exchanges it. That is the only
 * moment a pre-auth landing can be attributed to a player: the exchange is what
 * returns the identity, and a landing that fails the exchange never gets one —
 * which is exactly why publisher-reported drop-offs are unsearchable today.
 *
 * TELEMETRY ONLY. The signature is deliberately not verified here (the browser
 * has no key), so nothing read out of this may be trusted for authorization.
 * The gateway remains the only thing that validates the token.
 */
export interface HandoffTokenIdentity {
    playerId: string;
    playerName?: string;
    playerCountry?: string;
}

export const readHandoffTokenIdentity = (token: string): HandoffTokenIdentity | null => {
    const payload = decodeJwtPayload(token);
    if (!payload) {
        return null;
    }

    const playerId = asNonEmptyString(payload.playerId);
    if (!playerId) {
        return null;
    }

    return {
        playerId,
        playerName: asNonEmptyString(payload.playerName),
        playerCountry: asNonEmptyString(payload.playerCountry),
    };
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    try {
        const segments = token.split('.');
        if (segments.length !== 3) {
            return null;
        }
        const parsed: unknown = JSON.parse(decodeBase64Url(segments[1]));
        return isRecord(parsed) ? parsed : null;
    } catch {
        // A malformed or rotated token must never break the login it precedes.
        return null;
    }
};

// atob yields a binary string, which mangles the non-ASCII player names this
// travels with, so the bytes go through TextDecoder instead.
const decodeBase64Url = (segment: string): string => {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

// Publishers have shipped numeric player ids, so tolerate both shapes.
const asNonEmptyString = (value: unknown): string | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }
    return typeof value === 'string' && value.trim() !== '' ? value : undefined;
};
